import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../lib/auth';
import { useContractorAuthStore } from '../lib/contractorAuth';
import { API_BASE } from '../lib/apiBase';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
}

function HomeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}
function TaskIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
    </svg>
  );
}
function ShieldIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}
function VaultIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" /><circle cx="12" cy="14" r="2" />
    </svg>
  );
}
function InsightsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}
function GearIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  );
}
function LogoutIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}
function LeadsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  );
}
function AnalyticsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}

const HOMEOWNER_LINKS: NavItem[] = [
  { to: '/app/dashboard', label: 'Dashboard',  icon: <HomeIcon /> },
  { to: '/app/tasks',     label: 'Tasks',       icon: <TaskIcon /> },
  { to: '/app/safety',    label: 'Safety',      icon: <ShieldIcon /> },
  { to: '/app/vault',     label: 'Documents',   icon: <VaultIcon /> },
  { to: '/app/insights',  label: 'Insights',    icon: <InsightsIcon /> },
];

const CONTRACTOR_LINKS: NavItem[] = [
  { to: '/app/contractor/dashboard', label: 'Dashboard', icon: <HomeIcon /> },
  { to: '/app/contractor/leads',     label: 'Leads',     icon: <LeadsIcon /> },
  { to: '/app/contractor/analytics', label: 'Analytics', icon: <AnalyticsIcon /> },
];

const linkStyle = (isActive: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 14px',
  borderRadius: 10,
  textDecoration: 'none',
  fontSize: 14,
  fontWeight: 600,
  color: isActive ? '#9333EA' : '#374151',
  background: isActive ? '#F3E8FF' : 'transparent',
  transition: 'background 0.15s, color 0.15s',
});

export function SideNav() {
  const navigate    = useNavigate();
  const user        = useAuthStore((s) => s.user);
  const logout      = useAuthStore((s) => s.logout);
  const contractor  = useContractorAuthStore((s) => s.contractor);
  const cLogout     = useContractorAuthStore((s) => s.logout);

  const isContractor = !!contractor;
  const displayName  = isContractor ? contractor.business_name : (user?.name ?? 'Account');
  const displayEmail = isContractor ? contractor.email : (user?.email ?? '');
  const links        = isContractor ? CONTRACTOR_LINKS : HOMEOWNER_LINKS;
  const settingsPath = '/app/settings';

  function handleLogout() {
    if (isContractor) {
      cLogout();
      fetch(`${API_BASE}/contractor/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
      navigate('/contractor/login', { replace: true });
    } else {
      logout();
      fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
      navigate('/login', { replace: true });
    }
  }

  return (
    <aside style={{
      position: 'fixed',
      top: 0,
      left: 0,
      bottom: 0,
      width: 240,
      background: '#fff',
      borderRight: '1px solid #e5e7eb',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 100,
      padding: '20px 12px',
    }}>
      {/* Logo */}
      <div style={{ padding: '8px 14px 24px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 36, height: 36, background: '#9333EA', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
          {isContractor ? '🔧' : '🏠'}
        </div>
        <span style={{ fontWeight: 800, fontSize: 17, color: '#111827', letterSpacing: '-0.01em' }}>Housewizrd</span>
      </div>

      {/* Nav links */}
      <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {links.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            style={({ isActive }) => linkStyle(isActive)}
          >
            {item.icon}
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* Bottom: Settings + user info */}
      <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <NavLink to={settingsPath} style={({ isActive }) => linkStyle(isActive)}>
          <GearIcon />
          Settings
        </NavLink>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px 4px' }}>
          <div style={{
            width: 34, height: 34, borderRadius: '50%', background: '#9333EA',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 14, flexShrink: 0,
          }}>
            {displayName.charAt(0).toUpperCase()}
          </div>
          <div style={{ overflow: 'hidden' }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName}
            </p>
            <p style={{ margin: 0, fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayEmail}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleLogout}
          style={{ ...linkStyle(false), border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer', color: '#6b7280' }}
        >
          <LogoutIcon />
          Log out
        </button>

        <Link
          to="/early-access"
          style={{ display: 'block', padding: '6px 14px', fontSize: 11, color: '#d1d5db', textDecoration: 'none', textAlign: 'center' }}
        >
          Early Access
        </Link>
      </div>
    </aside>
  );
}
